/**
 * 名分主張 — 公開深度頁(不依賴 JWT,類似 BrandFounder / BrandEvent)
 *
 * 從 LINE「霽雲・知遇」入口卡的「✨ 名分主張」entry → reply 一張 bubble(精簡 hero +
 * lead quote)+ 底部「閱讀完整主張」按鈕跳這頁。
 *
 * 雜誌風結構:Header eyebrow + 3:4 直幅 hero + 大標 + 珊瑚橘 lead quote + 開場 +
 * 四步驟溯源軌跡(中文數字圓標 + 名稱)+ 中段 3:4 直幅圖 + 中間 quote + ISO 收尾段 + tag chips
 */
import { useEffect } from "react";


const GCS_BASE = "https://storage.googleapis.com/yannwo-product-images/brand";

const STEPS = [
  { name: "燕屋採摘", caption: "Sourcing" },
  { name: "無塵室工藝", caption: "Cleanroom" },
  { name: "手工挑毛", caption: "Handpicked" },
  { name: "營養檢測", caption: "Lab-Tested" },
];

const TAGS = ["純粹", "無添加", "安心", "生活"];


export default function BrandNaming() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream pb-20">
      {/* Header eyebrow */}
      <header className="bg-primary-dark text-white px-6 py-8 text-center">
        <div className="text-[10px] tracking-[0.3em] opacity-70 uppercase">
          霽雲 · 恆時珍藏
        </div>
        <div className="text-xs mt-2 opacity-75 tracking-widest uppercase">
          The Naming Pledge
        </div>
      </header>

      {/* Hero 3:4 直幅 */}
      <img
        src={`${GCS_BASE}/naming-pledge-portrait.jpg`}
        alt="霽雲 — 產品溯源"
        className="w-full aspect-[3/4] object-cover"
      />

      {/* Title */}
      <div className="px-6 pt-8 pb-2">
        <h1 className="text-2xl font-bold text-primary-dark tracking-wide">
          賦予每一片燕窩「名分」
        </h1>
        <div className="text-xs text-gray-mid mt-2 tracking-wider">
          Every nest deserves its name.
        </div>
      </div>

      {/* Lead quote */}
      <blockquote className="mx-6 my-6 border-l-[3px] border-coral pl-5 py-1">
        <p className="text-base leading-relaxed text-primary-dark">
          真正的高端,從來不是華麗堆疊,而是每一個細節,都經得起被看見。
        </p>
      </blockquote>

      {/* Intro */}
      <div className="px-6 space-y-4 text-[15px] leading-loose text-charcoal/85">
        <p>
          霽雲攜手全馬首家電子溯源燕農合作社,從燕屋採摘、無塵室工藝、手工挑毛,到營養檢測,每一步,都留下清晰可循的軌跡。
        </p>
      </div>

      {/* 四步驟溯源軌跡 */}
      <div className="px-6 mt-10">
        <div className="text-[10px] tracking-[0.3em] text-sage uppercase text-center">
          Traceable Process
        </div>
        <h3 className="text-base font-bold text-primary-dark text-center mt-2 mb-5 tracking-wide">
          四步驟溯源軌跡
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {STEPS.map((s, i) => (
            <div
              key={s.name}
              className="bg-white rounded-lg px-4 py-5 border border-sage-light/40 shadow-[0_1px_3px_rgba(0,0,0,0.03)] text-center"
            >
              {/* 圓標已是步驟序號,原本下方的「STEP 01」英文序號拿掉,避免同一張卡編號講兩次 */}
              <div className="w-9 h-9 mx-auto mb-2 rounded-full border border-primary text-primary flex items-center justify-center text-sm font-medium">
                {"一二三四"[i]}
              </div>
              <div className="text-sm font-bold text-primary-dark mt-1">
                {s.name}
              </div>
              <div className="text-[10px] text-gray-mid mt-1 tracking-wider">
                {s.caption}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mid image 3:4 直幅 */}
      <div className="mt-10">
        <img
          src={`${GCS_BASE}/naming-pledge-2-portrait.jpg`}
          alt="霽雲 — 產品溯源(現場)"
          className="w-full aspect-[3/4] object-cover"
        />
      </div>

      {/* Mid quote */}
      <blockquote className="mx-6 mt-8 mb-6 border-l-[3px] border-coral pl-5 py-1">
        <p className="text-base leading-relaxed text-primary-dark">
          信任,不該只是口頭承諾,而是能被親眼驗證的純淨。
        </p>
      </blockquote>

      {/* Closing — ISO + 收尾 */}
      <div className="px-6 space-y-4 text-[15px] leading-loose text-charcoal/85">
        <p>
          東南亞唯一 ISO 認證無塵室燕窩工廠處理,讓每一次滋養,回歸最純粹的安心。
        </p>
        <p className="text-primary-dark font-medium pt-2">
          霽雲,讓世界重新理解東方養生。
        </p>
      </div>

      {/* Tag chips */}
      <div className="px-6 mt-8 flex flex-wrap gap-2 justify-center">
        {TAGS.map((tag) => (
          <span
            key={tag}
            className="text-[11px] tracking-widest text-sage px-3 py-1 border border-sage-light/60 rounded-full"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Footer */}
      <footer className="text-center py-10 mt-4">
        <div className="text-[11px] text-gray-mid tracking-[0.3em]">
          霽雲 · 恆時珍藏
        </div>
      </footer>
    </div>
  );
}
